import { useTranslation } from "react-i18next";

import type { ChaseNotificationSchema } from "@/services/chase-notifications";
import { isChaseWinNotification } from "@/services/chase-notifications";
import type { PublicCampaignSchema } from "@/types";
import { Notification } from "./notification";
import { campaignKey } from "@/campaigns";
import { useAppStore } from "@/stores";
import { useImageUrl } from "@/hooks/use-image-url";
import { useWidget } from "@/hooks/widget";
import { useCampaignNavigation } from "@/contexts/campaign-navigation";

/**
 * A chase event as a toast: the rung the player just won on the gold prize card ("You won ·
 * €50 Bonus"), or the quiet note that the run was reset because the spins ran past the last
 * prize still open. Tapping it opens the widget on the campaign; the CTA only appears once the
 * `campaigns` stream knows that campaign — the same race guard as `JackpotToast`.
 */
export const ChaseToast = ({
  notification,
  onDismiss,
}: {
  notification: ChaseNotificationSchema;
  onDismiss: () => void;
}) => {
  const { t } = useTranslation();

  const campaign = useAppStore((state) =>
    Object.values(state.campaignsById).find(
      (candidate: PublicCampaignSchema) =>
        candidate.id === notification.campaign_id
    )
  );
  const imageUrl = useImageUrl(campaign?.image);
  const { open: openWidget } = useWidget();
  const navigateToCampaign = useCampaignNavigation();

  const openCampaign = () => {
    if (!campaign) return;
    onDismiss();
    openWidget();
    navigateToCampaign(campaignKey(campaign));
  };

  if (isChaseWinNotification(notification)) {
    return (
      <Notification
        // `prizes.youWon` ships translated in every locale; a reward reads the same everywhere.
        label={t("prizes.youWon")}
        title={notification.prize_title}
        imageUrl={imageUrl}
        variant="prize"
        onClick={campaign ? openCampaign : undefined}
      />
    );
  }

  return (
    <Notification
      title={t("notification.chase.resetTitle", "Out of spins for that prize")}
      text={t(
        "notification.chase.resetText",
        "You're back at the start — the chase goes on."
      )}
      imageUrl={imageUrl}
      variant="default"
      onClick={campaign ? openCampaign : undefined}
    />
  );
};
